/* eslint-disable no-prototype-builtins */
import { useCallback } from 'react';

import isEmpty from 'lodash-es/isEmpty';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';

import { setClipboard } from '@/redux/clipBoard/actions';
import { clipboardSelector } from '@/redux/clipBoard/selectors';
import { pasteAction, pasteCondition, pasteNode, pasteRequisite } from '@/redux/nodes/actions';
import { Action } from '@/shared/types/action';
import { Condition, Node, PasteTarget, Requisite } from '@/shared/types/node';
import { RootState } from '@/shared/types/state';
import { LAST_ACTIONS } from '@/util/constants';
import { CopyPasteController as CP, CopiedObject } from '@/util/CopyPasteController';

const pasteTargetOf = (target?: CopiedObject): PasteTarget | undefined => {
  if (!target) {
    return undefined;
  }

  switch (CP.getObjectType(target)) {
    case 'node':
      return { nodeId: (target as Node).node_id };
    case 'action':
      return { actionId: (target as Action).action_id };
    case 'condition':
      return { conditionId: (target as Condition).condition_id };
    case 'requisite':
      return { requisiteId: (target as Requisite).requisite_id };
    default:
      return undefined;
  }
};

export const useCopyPaste = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const copiedObjectRedux = useSelector(clipboardSelector);
  const rootId = useSelector((state: RootState) => state.nodes.nodes[0]?.node_id);
  const copyToClipboard = (obj) => navigator.clipboard.writeText(JSON.stringify(obj));

  const readFromClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();

      if (text) {
        return JSON.parse(text);
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const handleSuccessfulTemporalToast = useCallback(
    (type: string, actionType: string, menuOption: 'copied' | 'pasted') => {
      switch (type) {
        case 'node':
          toast.success(t(`temporal_toast.${menuOption}`, { 0: t('common.node') }));
          break;
        case 'action':
          toast.success(t(`temporal_toast.${menuOption}`, { 0: t(`actions.types.${actionType}`) }));
          break;
        case 'condition':
          toast.success(t(`temporal_toast.${menuOption}`, { 0: t('actions.types.condition') }));
          break;
        case 'requisite':
          toast.success(t(`temporal_toast.${menuOption}`, { 0: t('actions.types.question') }));
          break;
        case 'unknown':
          break;
      }
    },
    [t]
  );

  const handleCopy = useCallback(
    (obj) => {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }

      const objType = CP.getObjectType(obj as CopiedObject);

      if (obj.hasOwnProperty('type') && LAST_ACTIONS.includes(obj.type)) {
        toast.error(t('temporal_toast.cannot_be_copied', { 0: t(`actions.types.${obj.type}`) }));
        return;
      }

      let modifiedObj = CP.addKey(obj, 'moveo-signature', 'moveo');
      // Remove 'parent_id' and 'intent' keys if object type is 'node'
      if (objType === 'node') {
        modifiedObj = ['parent_id', 'intent'].reduce(CP.removeKey, modifiedObj);
      }

      const isClipboardAPIAvailable = navigator.clipboard && navigator.clipboard.readText;

      // Copy the modified object to the clipboard
      copyToClipboard(modifiedObj);

      const actionType = (modifiedObj as Action).type;
      handleSuccessfulTemporalToast(objType, actionType, 'copied');

      // If Clipboard API is not supported, store the object in redux as a fallback
      if (!isClipboardAPIAvailable) {
        dispatch(setClipboard(modifiedObj));
      }
    },
    [dispatch, handleSuccessfulTemporalToast, t]
  );

  const hanndlePasteAction = useCallback(
    (type: string, updatedPasteData: CopiedObject, target: PasteTarget) => {
      // Block pasting of actions that are not allowed
      const blockPaste = LAST_ACTIONS.includes(type);

      if (blockPaste) {
        toast.error(t('temporal_toast.cannot_be_pasted', { 0: t(`actions.types.${type}`) }));

        return;
      }

      dispatch(pasteAction({ newAction: updatedPasteData, target }));
      handleSuccessfulTemporalToast('action', type, 'pasted');
    },
    [dispatch, handleSuccessfulTemporalToast, t]
  );

  const handlePaste = useCallback(
    async (targetObject?: CopiedObject) => {
      try {
        let clipboardData = await readFromClipboard();
        let pasteData;

        if (!clipboardData && isEmpty(copiedObjectRedux)) {
          return;
        }

        if (clipboardData) {
          const isMoveoObject = CP.checkSignature(clipboardData);
          if (isMoveoObject) {
            clipboardData = CP.removeKey(clipboardData, 'moveo-signature');
            pasteData = clipboardData;
          }
        }

        if (!pasteData && !isEmpty(copiedObjectRedux)) {
          pasteData = copiedObjectRedux;
        }

        if (!pasteData) {
          return;
        }

        const updatedPasteData = CP.updateIds(pasteData as CopiedObject);
        const category = CP.getObjectType(updatedPasteData as CopiedObject);
        const target = pasteTargetOf(targetObject);

        if (!rootId && category !== 'node') {
          return;
        }

        switch (category) {
          case 'node':
            dispatch(pasteNode({ node: updatedPasteData, target }));
            handleSuccessfulTemporalToast(category, null, 'pasted');
            break;
          case 'action':
            hanndlePasteAction((updatedPasteData as Action).type, updatedPasteData, target);
            break;
          case 'condition':
            dispatch(pasteCondition({ condition: updatedPasteData, target }));
            handleSuccessfulTemporalToast(category, null, 'pasted');
            break;
          case 'requisite':
            dispatch(pasteRequisite({ requisite: updatedPasteData, target }));
            handleSuccessfulTemporalToast(category, null, 'pasted');
            break;
          case 'unknown':
            break;
        }
      } catch (error) {
        toast.error(error.message);
      }
    },
    [
      readFromClipboard,
      copiedObjectRedux,
      rootId,
      dispatch,
      handleSuccessfulTemporalToast,
      hanndlePasteAction,
    ]
  );

  return { handlePaste, handleCopy };
};
